import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { AzureWebRTCSessionService } from '@/services/implementations/AzureWebRTCSessionService';
import { AzureWebSocketSessionService } from '@/services/implementations/AzureWebSocketSessionService';
import { getLanguageDisplayName } from '@/services/interfaces/Translation';
import { SessionConnectionStatus } from '@/services/interfaces/Session';
import type { SessionServiceConfig, SessionInfo } from '@/services/interfaces/Session';
import type { SessionEvent } from '@/services/interfaces/SessionEvent';
import type { MicrophoneService } from '@/services/implementations/MicrophoneService';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useFreeTrialContext } from '@/contexts/FreeTrialContext';
import { toast } from 'sonner';
import { apiManager } from '@/services/implementations/APIManager';
import type { ConversationTurn, UploadConversationRequest } from '@/types/backend';
import { 
  createInitialConversationState, 
  resetConversationState, 
  processIncrementalEvents,
  type ConversationState, 
  type LanguageConfig 
} from '../utils/conversationProcessor';

import { useTranslation } from '@/contexts/I18nContext';

// 处理指令模板中的占位符
function processInstructionTemplate(template: string, placeholders: Record<string, string>): string {
  if (!template) return '';
  
  let processedInstructions = template;
  
  // 替换所有占位符，支持 {{placeholder}} 和 {placeholder} 两种格式
  Object.entries(placeholders).forEach(([key, value]) => {
    const doublePattern = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
    const singlePattern = new RegExp(`\\{${key}\\}`, 'g');
    
    processedInstructions = processedInstructions
      .replace(doublePattern, value)
      .replace(singlePattern, value);
  });
  
  return processedInstructions;
}

// Define a union type for session services
type SessionService = AzureWebRTCSessionService | AzureWebSocketSessionService;

interface ServiceInstances {
  sessionServiceInstance: SessionService | null;
}

export const useWebRTCSessionViewModel = (
  backendUrl: string,
  microphoneService: MicrophoneService | null = null,
  serviceType: 'webrtc' | 'websocket'
) => {
  // Add unique ID for tracking
  const hookId = useRef(`VMHook_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`);
  
  // 使用全局语言配置
  const { sourceLanguage, targetLanguage } = useLanguage();
  const { refreshUserProfile, hasEnoughTokens, updateUserTokens, isAuthenticated } = useAuth();
  const { freeTrialStatus, isInFreeTrial } = useFreeTrialContext();
  
  console.log(`🏗️ [${hookId.current}] useWebRTCSessionViewModel - Constructor`);

  // State
  const [isSessionActive, setIsSessionActive] = useState<boolean>(false);
  const [sessionConnectionStatus, setSessionConnectionStatus] = useState<SessionConnectionStatus>(SessionConnectionStatus.Disconnected);
  const [sessionInfo, setSessionInfo] = useState<SessionInfo | null>(null);
  const [rtcEvents, setRtcEvents] = useState<SessionEvent[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const [isSessionServiceInitialized, setIsSessionServiceInitialized] = useState<boolean>(false);
  const [isStopping, setIsStopping] = useState<boolean>(false); // 🔑 新增：停止翻译的loading状态

  // 🔑 测试事件注入功能
  const [testEvents, setTestEvents] = useState<SessionEvent[]>([]);
  const [isUsingTestEvents, setIsUsingTestEvents] = useState<boolean>(false);

  // 🔑 对话数据注入功能 - 新增
  const [injectedConversationTurns, setInjectedConversationTurns] = useState<ConversationTurn[]>([]);
  const [isUsingInjectedConversations, setIsUsingInjectedConversations] = useState<boolean>(false);

  // 使用ref存储服务实例，确保只创建一次
  const servicesRef = useRef<ServiceInstances>({
    sessionServiceInstance: null
  });
  
  // 🔑 对话状态管理 - 集成到 WebRTC ViewModel 中
  const conversationStateRef = useRef<ConversationState>(createInitialConversationState());
  
  // 防抖处理状态
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [debouncedEventsLength, setDebouncedEventsLength] = useState(0);
  const debouncedEventsRef = useRef<SessionEvent[]>([]); // 🔑 新增：存储防抖时的事件快照
  
  // 🔑 新增：跟踪已处理的completed turn数量（用于token扣除）
  const processedTokenEventsRef = useRef<number>(0);

  // 🔑 测试事件注入方法
  const injectTestEvents = useCallback((events: SessionEvent[]) => {
    console.log(`🧪 [${hookId.current}] 注入测试事件:`, events.length);
    setTestEvents(events);
    setIsUsingTestEvents(true);
    // 重置对话状态以处理新的测试事件
    resetConversationData();
  }, [hookId]);

  // 🔑 清除测试事件，回到正常模式
  const clearTestEvents = useCallback(() => {
    console.log(`🧪 [${hookId.current}] 清除测试事件，回到正常模式`);
    setTestEvents([]);
    setIsUsingTestEvents(false);
    // 手动重置状态，避免依赖 resetConversationData
    resetConversationState(conversationStateRef.current);
    setDebouncedEventsLength(0);
  }, [hookId]);

  // 🔑 注入对话数据方法 - 新增
  const injectConversationTurns = useCallback((turns: ConversationTurn[]) => {
    console.log(`💬 [${hookId.current}] 注入对话数据:`, turns.length);
    setInjectedConversationTurns(turns);
    setIsUsingInjectedConversations(true);
    // 重置对话状态以显示注入的数据
    resetConversationData();
  }, [hookId]);

  // 🔑 清除注入的对话数据，回到正常模式
  const clearInjectedConversations = useCallback(() => {
    console.log(`💬 [${hookId.current}] 清除注入的对话数据，回到正常模式`);
    setInjectedConversationTurns([]);
    setIsUsingInjectedConversations(false);
    resetConversationData();
  }, [hookId]);

  // 🔑 获取当前使用的事件数组（测试事件或真实事件）
  const currentEvents = isUsingTestEvents ? testEvents : rtcEvents;

  const { t } = useTranslation();

  useEffect(() => {
    // 清除之前的定时器
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // 检查是否有新事件需要处理
    const hasNewEvents = rtcEvents.length > conversationStateRef.current.lastProcessedIndex + 1;
    if (!hasNewEvents) return;

    // 设置新的防抖定时器
    debounceTimerRef.current = setTimeout(() => {
      console.log(`⏰ [${hookId.current}] 防抖触发: 处理 ${rtcEvents.length - conversationStateRef.current.lastProcessedIndex - 1} 个新事件`);
      
      // 🔑 关键修复：创建当前事件的快照
      debouncedEventsRef.current = [...currentEvents];
      setDebouncedEventsLength(currentEvents.length);
    }, 300);

    // 清理函数
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [currentEvents.length, hookId]); // 只依赖事件数量，避免频繁触发

  // 🔑 对话数据处理 - 使用 useMemo 优化性能
  const conversationTurns = useMemo(() => {
    // 如果使用注入的对话数据，直接返回
    if (isUsingInjectedConversations) {
      return injectedConversationTurns;
    }
    
    // 🔑 关键修复：使用防抖时的事件快照，确保数据一致性
    const eventsToProcess = debouncedEventsRef.current;
    
    // 否则处理事件生成对话
    const languageConfig: LanguageConfig = { sourceLanguage, targetLanguage };
    const turns = processIncrementalEvents(eventsToProcess, languageConfig, conversationStateRef.current).sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    // 🔑 检测新完成的turn并扣除token - 只在实时翻译会话时执行
    const completedTurns = turns.filter(turn => turn.status === 'completed' && turn.usage);
    const newCompletedTurns = completedTurns.slice(0,completedTurns.length - processedTokenEventsRef.current);
    
    // 🔑 关键检查：只有在实时翻译会话时才执行 token 扣除逻辑
    if (newCompletedTurns.length > 0 && isSessionActive) {
      console.log(`💰 [${hookId.current}] Processing ${newCompletedTurns.length} new completed turns for token deduction (session active: ${isSessionActive})`);
      
      newCompletedTurns.forEach(turn => {
        if (turn.usage?.total_tokens) {
          console.log(`💰 [${hookId.current}] Deducting ${turn.usage.total_tokens} tokens for turn ${turn.id}`);
          updateUserTokens(turn.usage.total_tokens);
        }
      });
      
      // 更新已处理的完成turn数量
      processedTokenEventsRef.current = completedTurns.length;
    } else if (newCompletedTurns.length > 0 && !isSessionActive) {
      console.log(`🚫 [${hookId.current}] 检测到 ${newCompletedTurns.length} 个新完成的 turn，但会话未激活，跳过 token 扣除`);
      // 即使不扣费，也要更新计数器，避免后续重复处理
      processedTokenEventsRef.current = completedTurns.length;
    }
    
    console.log(`💰 [${hookId.current}] 我想看看处理后的对话数据:`, turns);

    return turns;
  }, [isUsingInjectedConversations, injectedConversationTurns, debouncedEventsLength, sourceLanguage, targetLanguage, updateUserTokens, isSessionActive, hookId]); // 🔑 添加 isSessionActive 依赖
  
  // 🔑 重置对话状态的方法
  const resetConversationData = useCallback(() => {
    console.log(`🔄 [${hookId.current}] 重置对话状态`);
    resetConversationState(conversationStateRef.current);
    setDebouncedEventsLength(0); // 重置防抖状态
    debouncedEventsRef.current = []; // 🔑 重置事件快照
    processedTokenEventsRef.current = 0; // 🔑 重置已处理的completed turn计数
  }, [hookId]);
  
  // 初始化API服务
  const initSessionService = useCallback(async () => {
    console.log(`🔧 [${hookId.current}] initSessionService - Starting initialization for ${serviceType}`);
    
    if (!servicesRef.current.sessionServiceInstance) {
      let service: SessionService;

      if (serviceType === 'websocket') {
        console.log(`🏗️ [${hookId.current}] Creating new AzureWebSocketSessionService instance`);
        service = new AzureWebSocketSessionService();
      } else {
        console.log(`🏗️ [${hookId.current}] Creating new AzureWebRTCSessionService instance`);
        service = new AzureWebRTCSessionService();
      }

      // 订阅错误事件
      service.subscribeToError((error) => {
        console.error(`🚨 [${hookId.current}] Session service error:`, error);
        setError(error);
      });

      await service.initialize(microphoneService);
      servicesRef.current.sessionServiceInstance = service;
      setIsSessionServiceInitialized(true);
      console.log(`✅ [${hookId.current}] initSessionService - Initialization completed for ${serviceType}`);
    }

    return servicesRef.current.sessionServiceInstance;
  }, [backendUrl, microphoneService, serviceType, hookId]);
  
  // 获取API服务实例
  const getSessionServiceInstance = useCallback(() => {
    return servicesRef.current.sessionServiceInstance;
  }, []);

  // 保存对话数据到后端
  const saveConversationToBackend = useCallback(async (sessionId: string, turns: ConversationTurn[]) => {
    try {
      if (!turns || turns.length === 0) {
        console.log(`📝 [${hookId.current}] 没有对话数据需要保存`);
        return;
      }

      console.log(`📝 [${hookId.current}] 开始保存对话数据到后端:`, {
        sessionId,
        turnsCount: turns.length
      });

      // 过滤出已完成的对话轮次，并转换为后端格式
      const completedTurns:Omit<ConversationTurn, 'id' | 'session_id'>[] = turns
        // .filter(turn => turn.status === 'completed' && turn.user_input.trim() && turn.assistant_output.trim())
        .map(turn => ({
          item_id: turn.item_id || turn.id,
          speaker: turn.speaker,
          user_input: turn.user_input,
          user_language: turn.user_language,
          assistant_output: turn.assistant_output,
          assistant_language: turn.assistant_language,
          status: turn.status as 'completed' | 'incomplete' | 'error',
          timestamp: turn.timestamp,
          usage_data: turn.usage_data,
          merged_from_item_ids: turn.merged_from_item_ids
        }));

      if (completedTurns.length === 0) {
        console.log(`📝 [${hookId.current}] 没有已完成的对话数据需要保存`);
        return;
      }

      const uploadRequest: UploadConversationRequest = {
        session_id: sessionId,
        conversation_turns: completedTurns
      };

      const response = await apiManager.uploadConversation(uploadRequest);
      
      console.log(`✅ [${hookId.current}] 对话数据保存成功:`, {
        tokensDeducted: response.tokens_deducted,
        remainingTokens: response.remaining_tokens,
        savedTurns: response.conversation_turns?.length || 0
      });

      // 显示保存成功的提示
      toast.success(`💾 对话已保存 (${completedTurns.length} 条记录, ${response.tokens_deducted} tokens)`);

      // 如果有警告信息，显示警告
      if (response.warning) {
        toast.warning(`${response.warning}⚠️ `);
      }

      // 🔑 保存成功后刷新用户配置文件信息
      try {
        await refreshUserProfile();
        console.log(`🔄 [${hookId.current}] 用户配置信息已刷新`);
      } catch (profileError) {
        console.error(`⚠️ [${hookId.current}] 刷新用户配置失败:`, profileError);
        // 不影响主流程，只记录错误
      }

    } catch (error) {
      console.error(`❌ [${hookId.current}] 保存对话数据失败:`, error);
      toast.error(`❌ 保存对话失败: ${error instanceof Error ? error.message : String(error)}`);
    }
  }, [hookId, refreshUserProfile]);

  useEffect(() => {
    console.log(`💾 [${hookId.current}] 监听到对话变化，让我看看conversationTurns`, conversationTurns);
  }, [conversationTurns, hookId]);

  // Stop translation session
  const stopTranslation = useCallback(async () => {
    console.log(`💾 [${hookId.current}] 停止翻译,让我看看conversationTurns`, conversationTurns);

    // 🔑 设置停止状态，显示loading
    setIsStopping(true);

    try {
      const sessionServiceInstance = getSessionServiceInstance();

      if (!sessionServiceInstance) {
        setIsStopping(false);
        return; // No service to stop
      }

      // 🔑 立即停止会话，快速响应用户操作
      console.log(`⚡ [${hookId.current}] 立即停止会话，快速退出`);
      await sessionServiceInstance.stopSession();

      // 🔑 重置停止状态，让UI快速响应
      setIsStopping(false);

      // 🔑 免费试用用户不保存对话数据（因为没有登录账户）
      if (!isAuthenticated && isInFreeTrial) {
        console.log(`🆓 [${hookId.current}] 免费试用用户，跳过保存对话数据`);
        return;
      }

      // 🔑 检查是否有有效的对话内容需要保存（仅限登录用户）
      const hasValidContent = conversationTurns.length > 0 &&
        conversationTurns.some(turn =>
          turn.status === 'completed' &&
          turn.user_input.trim() !== '' &&
          turn.assistant_output.trim() !== ''
        );

      // 🔑 异步保存对话数据，不阻塞UI退出
      if (sessionInfo?.chatSessionId && hasValidContent) {
        console.log(`💾 [${hookId.current}] 异步保存对话数据 (${conversationTurns.length} 条记录)`);
        // 使用 Promise 异步执行，不等待结果
        saveConversationToBackend(sessionInfo.chatSessionId, conversationTurns).catch(err => {
          console.error(`❌ [${hookId.current}] 异步保存对话数据失败:`, err);
          // 显示保存失败的提示，但不影响退出流程
          toast.error('💾 对话数据保存失败，但翻译已停止');
        });
      } else if (sessionInfo?.chatSessionId && conversationTurns.length > 0) {
        console.log(`�️ [${hookId.current}] 对话无有效内容，删除当前对话记录 (${conversationTurns.length} 条记录)`);
        deleteChatSessionWhenNoValidTurns();
      } else {
        console.log(`💾 [${hookId.current}] 无对话数据需要保存`);
        deleteChatSessionWhenNoValidTurns();
      }

    } catch (err) {
      setIsStopping(false);
      setError(err instanceof Error ? err : new Error(String(err)));
    }
  }, [getSessionServiceInstance, sessionInfo, conversationTurns, saveConversationToBackend, hookId, isInFreeTrial, isAuthenticated]);


  const deleteChatSessionWhenNoValidTurns = useCallback(async () => {
    if (sessionInfo?.chatSessionId) {
      // 异步删除无效的对话记录
      apiManager.deleteChatSession(sessionInfo.chatSessionId).catch(err => {
        console.error(`❌ [${hookId.current}] 删除无效对话记录失败:`, err);
        // 删除失败不影响用户体验，静默处理
      });
    }
  }, [sessionInfo, hookId]);

  // RTC events handler
  const handleRtcEvents = useCallback((events: SessionEvent[]) => {
    setRtcEvents(events);
    
    // Check for error events
    const errorEvents = events.filter(event => event.type === 'error');
    if (errorEvents.length > 0) {
      // Handle the most recent error event
      const latestError = errorEvents[errorEvents.length - 1];
      console.error(`🚨 [${hookId.current}] Error event received:`, latestError);
      
      // Extract error message from the event
      let errorMessage = 'An unknown error occurred';
      if (latestError.error?.message) {
        errorMessage = latestError.error.message;
      } else if (latestError.message) {
        errorMessage = latestError.message;
      }
      
      // Show toast notification
      toast.error(`Session Error: ${errorMessage}⚠️ `);
      
      // Stop the translation session
      stopTranslation().catch(err => {
        console.error(`❌ [${hookId.current}] Failed to stop translation after error:`, err);
      });
    }
  }, [hookId, stopTranslation]);

  // Status change handler
  const handleStatusChange = useCallback((status: SessionConnectionStatus, sessionInfo: SessionInfo | null) => {
    setSessionConnectionStatus(status);
    setSessionInfo(sessionInfo);
    setIsSessionActive(status === SessionConnectionStatus.Connected);
  }, []);

  // Start translation session
  const startTranslation = useCallback(async (microphoneDeviceId: string) => {
    try {
      clearError(); // 清除之前的错误
      
      // 🔑 检查用户是否有足够的 tokens
      if (!hasEnoughTokens()) {
        const errorMessage = t(`common.tokenNotEnough`);
        // 'Token 余额不足，请购买更多 tokens 或升级订阅';
        throw new Error(errorMessage);
      }
      
      // 🔑 重置对话状态 - 关键修复点
      resetConversationData();
      
      const sessionServiceInstance = getSessionServiceInstance();
      
      if (!sessionServiceInstance) {
        throw new Error('sessionServiceInstance not initialized');
      }
      
      // Subscribe to RTC events and status changes
      sessionServiceInstance.subscribeToRtcEvents(handleRtcEvents);
      sessionServiceInstance.subscribeToStatusChange(handleStatusChange);

      // Get friendly language names
      const sourceLangName = getLanguageDisplayName(sourceLanguage);
      const targetLangName = getLanguageDisplayName(targetLanguage);
        
      const displaySourceName = t(`language.${sourceLangName}`);
      const displayTargetName = t(`language.${targetLangName}`);

      const config: SessionServiceConfig = {
        voice: 'verse',
        sourceLanguage: sourceLanguage,
        targetLanguage: targetLanguage,
        microphoneDeviceId: microphoneDeviceId,
        instructions: processInstructionTemplate(import.meta.env.VITE_TRANSLATOR_INSTRUCTIONS, {
          'sourceLangName': displaySourceName,
          'targetLangName': displayTargetName
        })
      };
      
      // 🔑 获取免费试用的 trial ID（如果是免费试用用户）
      const trialId = isInFreeTrial && freeTrialStatus?.trialId ? freeTrialStatus.trialId : undefined;
      console.log(`🔑 [${hookId.current}] 免费试用用户，trialId: ${trialId}`);
      // Start session with Azure Realtime API
      await sessionServiceInstance.startSession(config, trialId);

    } catch (err) {
      console.error(`❌ [${hookId.current}] 启动翻译会话失败:`, err);
      setError(err instanceof Error ? err : new Error(String(err)));
      setIsSessionActive(false);
      setSessionConnectionStatus(SessionConnectionStatus.Error);
    }
  }, [getSessionServiceInstance, handleRtcEvents, handleStatusChange, sourceLanguage, targetLanguage, resetConversationData, hasEnoughTokens, isInFreeTrial, freeTrialStatus?.trialId]);

  // 🔑 新增：清除错误状态的方法
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      console.log(`🗑️ [${hookId.current}] useWebRTCSessionViewModel - Starting cleanup`);
      // 使用已存在的实例进行清理
      const { sessionServiceInstance } = servicesRef.current;

      if (sessionServiceInstance) {
        console.log(`💥 [${hookId.current}] Destroying session service instance`);
        // Use destroy method for complete cleanup
        sessionServiceInstance.destroy().catch(console.error);
      }

      // 清空服务引用
      servicesRef.current = {
        sessionServiceInstance: null
      };

      console.log(`💀 [${hookId.current}] useWebRTCSessionViewModel - Destructor completed`);
    };
  }, [hookId]);

  // 初始化服务
  useEffect(() => {
    // Only initialize if we have a microphone service
    if (microphoneService) {
      console.log(`🔧 [${hookId.current}] Starting service initialization with microphone service`);
      initSessionService().catch(err => {
        console.error(`❌ [${hookId.current}] Failed to initialize API service:`, err);
        setError(err instanceof Error ? err : new Error(String(err)));
      });
    } else {
      console.log(`⏳ [${hookId.current}] Waiting for microphone service before initialization`);
    }
  }, [initSessionService, microphoneService, hookId]);

  return {
    isSessionActive,
    sessionConnectionStatus,
    sessionInfo,
    rtcEvents,
    error,
    isSessionServiceInitialized,
    isStopping, // 🔑 新增：停止翻译的loading状态
    startTranslation,
    stopTranslation,
    getSessionServiceInstance,
    clearError, // 🔑 新增：清除错误的方法
    // 🔑 新增：对话数据相关
    conversationTurns,
    resetConversationData,
    // 🔑 测试事件相关
    injectTestEvents,
    clearTestEvents,
    injectConversationTurns,
    clearInjectedConversations
  };
};